import React from 'react';
import {MessageDescriptor} from 'react-intl';
import {BehaviorSubject, distinctUntilChanged, Observable, skip} from 'rxjs';
import {MenuItem, WidgetHeaderAPI, WidgetMenuAPI} from '.';

export class Menu implements WidgetMenuAPI {
  private items$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>(
    [],
  );

  items = (): MenuItem[] => {
    return this.items$.value;
  };

  itemsObservable = (): Observable<MenuItem[]> => {
    return this.items$.asObservable().pipe(
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  setMenuItems = (items: MenuItem[]) => {
    this.items$.next(items);
  };
}

export class Header implements WidgetHeaderAPI {
  private title$: BehaviorSubject<MessageDescriptor | React.ReactNode> =
    new BehaviorSubject<MessageDescriptor | React.ReactNode>(undefined);

  private visibility$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true,
  );

  private disabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  private topBar$: BehaviorSubject<React.ReactNode> =
    new BehaviorSubject<React.ReactNode>(null);

  constructor(visibility = true, disabled = false) {
    this.visibility$.next(visibility);
    this.disabled$.next(disabled);
    this.topBar$.next(null);
  }

  title = (): MessageDescriptor | React.ReactNode => {
    return this.title$.value;
  };

  isVisible = (): boolean => {
    return this.visibility$.getValue();
  };

  isDisabled = (): boolean => {
    return this.disabled$.getValue();
  };

  getTopBar = (): React.ReactNode => {
    return this.topBar$.getValue();
  };

  titleObservable = (): Observable<MessageDescriptor | React.ReactNode> => {
    return this.title$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  visibilityObservable = (): Observable<boolean> => {
    return this.visibility$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  disabledObservable = (): Observable<boolean> => {
    return this.disabled$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  topBarObservable = (): Observable<React.ReactNode> => {
    return this.topBar$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  };

  setTitle = (title: MessageDescriptor | React.ReactNode) => {
    if (title === null) {
      this.title$.next(undefined);
    } else {
      this.title$.next(title);
    }
  };

  setVisibility = (visible: boolean) => {
    this.visibility$.next(visible);
  };

  setDisabled = (disabled: boolean) => {
    this.disabled$.next(disabled);
  };

  setTopBar = (topBar: React.ReactNode) => {
    this.topBar$.next(topBar);
  };
}
